import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ListOrdered, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MovieDetails } from "@/components/MovieDetails";
import { SeenRow } from "@/components/SeenRow";
import { SeenHint } from "@/components/SeenHint";
import { useHint } from "@/hooks/useHint";

const EMPTY = {
  liked: "Nothing here yet. Swipe up on movies you've seen and liked.",
  disliked: "Nothing here yet. Swipe down on movies you've seen and didn't like.",
  passed: "Nothing here yet. Swipe left on movies you're not interested in.",
};

function orderLabel(kind, data) {
  if (kind === "passed") return `${data.items.length} skipped with a left swipe · never shown again`;
  return data.ranked && kind === "liked" ? "Your ranking" : "Most recent first";
}

export default function Seen() {
  const [kind, setKind] = useState("liked");
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(null);
  const hint = useHint(`seen-${kind}`);

  const load = useCallback(async () => {
    const { data } = await api.get("/seen", { params: { kind } });
    setData(data);
  }, [kind]);
  useEffect(() => { setData(null); load(); }, [load]);

  const move = async (movie, action) => {
    hint.dismiss();
    setData((d) => ({ ...d, items: d.items.filter((m) => m.movie_id !== movie.movie_id) }));
    try {
      await api.post("/swipe", { movie_id: movie.movie_id, direction: action.direction, source: "seen" });
      toast(action.toast, {
        id: "seen-feedback", description: movie.title, duration: 4000,
        action: { label: "Undo", onClick: () => api.post("/swipe/undo", { movie_id: movie.movie_id }).then(load).catch((e) => toast.error(formatApiError(e))) },
      });
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const hasItems = data?.items.length > 0;
  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader eyebrow="Your history" title="Seen" />
      <Tabs value={kind} onValueChange={setKind} className="mb-6">
        <TabsList className="rounded-full h-11">
          <TabsTrigger value="liked" className="rounded-full px-5" data-testid="seen-tab-liked">Liked</TabsTrigger>
          <TabsTrigger value="disliked" className="rounded-full px-5" data-testid="seen-tab-disliked">Didn't like</TabsTrigger>
          <TabsTrigger value="passed" className="rounded-full px-5" data-testid="seen-tab-passed">Not interested</TabsTrigger>
        </TabsList>
      </Tabs>
      {!data ? (
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      ) : !hasItems ? (
        <div className="rounded-3xl border border-dashed p-12 text-center text-muted-foreground" data-testid="seen-empty">{EMPTY[kind]}</div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold" data-testid="seen-order-label">{orderLabel(kind, data)}</p>
            {kind === "liked" && (
              <Link to="/rank" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary shrink-0" data-testid="seen-rank-link">
                <ListOrdered className="h-4 w-4" /> Edit ranking
              </Link>
            )}
          </div>
          <ul className="space-y-3" data-testid={`seen-list-${kind}`}>
            <AnimatePresence initial={false}>
              {data.items.map((m, i) => (
                <motion.div key={m.movie_id} layout exit={{ height: 0, opacity: 0, marginTop: 0 }} transition={{ duration: 0.2 }}>
                  <SeenRow movie={m} kind={kind} rank={data.ranked && kind === "liked" ? i + 1 : null} onOpen={setDetails} onMove={move} />
                </motion.div>
              ))}
            </AnimatePresence>
          </ul>
        </>
      )}
      <SeenHint kind={kind} open={hasItems && !hint.dismissed} onDismiss={hint.dismiss} />
      <MovieDetails movie={details} onClose={() => setDetails(null)} />
    </div>
  );
}
